import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { ProtectedRoute } from "@gyandiksha/shared-ui/routes";
import { getAuthProps } from "@/services";
import { PROFILESTATUS } from "@gyandiksha/shared-ui";

/**
 * Custom Route Guard to check student's APAAR / APPER ID existence and access rights.
 * 
 * Rules:
 * 1. If student APPER ID is NOT found:
 *    Access DENIED for:
 *    - /portal-selection
 *    - /student-registration
 *    - /student-registration-review
 *    Redirects back in history (navigate(-1)) if history exists,
 *    otherwise redirects to /apaar-verification.
 * 
 * 2. If student APPER ID EXISTS:
 *    Allows access to /student-registration and /student-registration-review.
 *    If accessing /portal-selection before completing registration/review,
 *    redirects to /student-registration.
 */
const ApaarProtectedRoute = ({ children, sidebarMenuConfig }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const authState = useSelector((state) => state.auth);
    const authData = getAuthProps() || authState?.authData || {};

    const isLogedin = !!authData?.token || authState?.isLogedin;

    // Check if apaarId exists
    const apaarId = authState?.apaarId || authData?.apaarId;
    const hasApaarId = Boolean(apaarId && String(apaarId).trim() !== "");

    useEffect(() => {
        if (isLogedin && !hasApaarId) {
            navigate("/apaar-verification", { replace: true });
        }
    }, [isLogedin, hasApaarId, navigate, location.pathname]);

    // If not logged in at all, defer to standard ProtectedRoute to handle login redirect
    if (!isLogedin) {
        return (
            <ProtectedRoute sidebarMenuConfig={sidebarMenuConfig}>
                {children}
            </ProtectedRoute>
        );
    }

    // Rule 1: If apperid is NOT found -> Access DENIED, return null while useEffect navigates back/redirects
    if (!hasApaarId) {
        return null;
    }

    // Rule 2: If apperid EXISTS and user tries to go to portal selection without completing review/verification
    const isReviewed = authState?.reviewed ?? authData?.reviewed;
    const isVerified = (authState?.authData?.profileStatus || authData?.profileStatus) === PROFILESTATUS?.VERIFIED;

    if (location.pathname === "/portal-selection" && (!isReviewed || !isVerified)) {
        return <Navigate to="/student-registration" replace />;
    }

    return (
        <ProtectedRoute sidebarMenuConfig={sidebarMenuConfig}>
            {children}
        </ProtectedRoute>
    );
};

export default ApaarProtectedRoute;
